export interface QueueTask<TResult> {
  run: () => Promise<TResult>;
}

interface QueueState {
  queue: QueueTask<unknown>[];
  running: boolean;
}

const buckets = new Map<string, QueueState>();

const drainBucket = async (nodeId: string): Promise<void> => {
  const state = buckets.get(nodeId);
  if (!state || state.running) return;
  state.running = true;
  try {
    while (state.queue.length > 0) {
      const task = state.queue.shift();
      if (!task) break;
      try {
        await task.run();
      } catch {
        console.error('Failed draining bucket.');
      }
    }
  } finally {
    state.running = false;
    if (state.queue.length === 0) buckets.delete(nodeId);
  }
};

export const enqueue = <T>(
  nodeId: string,
  task: QueueTask<T>,
  position: 'tail' | 'head' = 'tail',
): void => {
  let state = buckets.get(nodeId);
  if (!state) {
    state = { queue: [], running: false };
    buckets.set(nodeId, state);
  }
  if (position === 'head') state.queue.unshift(task as QueueTask<unknown>);
  else state.queue.push(task as QueueTask<unknown>);
  void drainBucket(nodeId);
};

export const pendingFor = (nodeId: string): number => {
  const state = buckets.get(nodeId);
  if (!state) return 0;
  return state.queue.length + (state.running ? 1 : 0);
};

export const drain = async (): Promise<void> => {
  while (buckets.size > 0) {
    const ids = Array.from(buckets.keys());
    await Promise.all(ids.map(id => drainBucket(id)));
    if (
      Array.from(buckets.values()).every(
        s => s.queue.length === 0 && !s.running,
      )
    ) {
      break;
    }
  }
};
